@let branchData = branch();

@if (this.isLoadingBranchLocations()) {
  <app-pan-blank-slate>
    <app-pan-spinner></app-pan-spinner>
    <span>Loading branch...</span>
  </app-pan-blank-slate>
} @else {
  <div>
    <div class="flex flex-col items-center py-1 mb-1 gap-4">
      <div class="grid grid-cols-3 gap-2 w-full">
        <div class="col-span-1 flex gap-2 items-end">
          <app-pan-form-control>
            <app-pan-floating-label>Customer Tag</app-pan-floating-label>
            <div class="flex flex-row items-center gap-2">
              <ng-select
                class="w-full"
                [placeholder]="canManageCustomerTags() ? 'Select or type new customer...' : 'Select customer...'"
                [items]="sortedCustomerChoices()"
                [bindLabel]="'name'"
                [bindValue]="'id'"
                [(ngModel)]="customerId"
                [typeahead]="customerSubject"
                [addTag]="canManageCustomerTags()"
                [loading]="isLoadingCustomers() || isPendingCustomer()"
                [disabled]="isLoadingCustomers()"
                (change)="onCustomerChange($event)"
              >
                <ng-template ng-tag-tmp let-search="searchTerm"> + New Customer Tag: {{ search }} </ng-template>
              </ng-select>

              @if (branchCustomer() && canManageCustomerTags()) {
                <app-pan-button color="warning" icon="delete" (click)="removeCustomerSwal.fire()"></app-pan-button>
              }
            </div>
          </app-pan-form-control>

          <div class="col-span-1 flex items-end">
            <app-pan-chip color="info" size="lg"> Customer Type: {{ getCustomerTypeName(branchData) | uppercase }} </app-pan-chip>
          </div>
        </div>

        <div class="col-span-1">
          <app-pan-form-control>
            <app-pan-floating-label>Business Vertical</app-pan-floating-label>
            <ng-select
              appendTo="body"
              placeholder="Select business vertical..."
              [bindValue]="'id'"
              [bindLabel]="'name'"
              [virtualScroll]="true"
              [appendTo]="'body'"
              [disabled]="isSavingBusinessVertical()"
              [items]="validBusinessVerticals()"
              [(ngModel)]="branchData.business_vertical.id"
              (change)="changeBusinessVertical($event)"
            >
            </ng-select>
          </app-pan-form-control>
        </div>
      </div>

      @if (customersError()) {
        <app-pan-inline-alert type="danger">{{ customersError() }}</app-pan-inline-alert>
      }

      <app-pan-section-header class="w-full" [title]="'Attributes'"></app-pan-section-header>

      <div class="space-y-4 ml-auto mr-auto w-full">
        <fieldset class="border border-solid border-gray-300 p-3">
          <legend class="font-bold">Branch Metadata</legend>

          <div class="flex flex-wrap gap-2">
            <label class="w-[30%]" matTooltip="Prioritize reviews for this branch">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'vip')"
                [ngModel]="branchData.keystore?.vip ?? false"
              />
              VIP
            </label>

            <label class="w-[30%]" matTooltip="Mark branch as an auto-renew branch">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'auto_renew')"
                [ngModel]="branchData.keystore?.auto_renew ?? false"
              />
              Auto-renew
            </label>

            <label
              class="w-[30%]"
              matTooltip="(Currently Canteen Only) Turning this on adds an explanation in the shared incident report email that videos are only for security and loss prevention"
            >
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'denote_policy_in_emails')"
                [ngModel]="branchData.keystore?.denote_policy_in_emails ?? false"
              />
              Denote Policy In Emails
            </label>
          </div>
        </fieldset>

        <fieldset class="border border-solid border-gray-300 p-3">
          <legend class="font-bold">Branch Features</legend>

          <div class="flex flex-wrap gap-2">
            <label
              class="w-[30%]"
              matTooltip="Suspend all operational access and pause every active plan for this branch"
              panRole
              [roles]="['admin']"
            >
              <input
                type="checkbox"
                panCheckbox
                color="warning"
                (click)="$event.preventDefault(); suspendAccess.fire()"
                [checked]="branchData.keystore?.suspend_access ?? false"
              />
              Suspend Access
            </label>

            <label class="w-[30%]" matTooltip="Enables facial recognition">
              <input
                type="checkbox"
                panCheckbox
                color="warning"
                (click)="$event.preventDefault(); facialRecognition.fire()"
                [checked]="branchData.face_recognition_enabled ?? false"
              />
              Face Recognition
            </label>

            <label class="w-[30%]" matTooltip="Hides $ value in Theft Report for all locations of this branch">
              <input
                type="checkbox"
                panCheckbox
                (click)="$event.preventDefault(); dollarsHide.fire()"
                [checked]="branchData.keystore?.hide_currency ?? false"
              />
              Hide Currency
            </label>

            <label class="w-[30%]" matTooltip="This hides or unhides the None template in the Incident Submission tab">
              <input
                type="checkbox"
                panCheckbox
                (click)="$event.preventDefault(); disableNoneTemplate.fire()"
                [checked]="branchData.keystore?.disable_none_template ?? false"
              />
              Disable None Template (Incident Submission)
            </label>

            <label class="w-[30%]" matTooltip="Incident submissions require QA">
              <input
                type="checkbox"
                panCheckbox
                (click)="$event.preventDefault(); requiresQa.fire()"
                [checked]="branchData.keystore?.incident_submission_requires_qa ?? false"
              />
              Require QA
            </label>

            <label class="w-[30%]" matTooltip="If enabled, guest users will receive a weekly incident report">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'guest_weekly_notification')"
                [ngModel]="branchData.keystore?.guest_weekly_notification ?? false"
              />
              Guest Weekly Notification
            </label>

            <label class="w-[30%]" matTooltip="If enabled, camera names will remain as they are in the external provider">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'disable_camera_renaming')"
                [ngModel]="branchData.keystore?.disable_camera_renaming ?? false"
              />
              Disable Camera Renaming
            </label>

            <label class="w-[30%]" matTooltip="If enabled, reviews are auto-validated and marked final without Panoptyc QA">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'auto_validate_reviews')"
                [ngModel]="branchData.keystore?.auto_validate_reviews ?? false"
              />
              Skip QA
            </label>

            <label class="w-[30%]" matTooltip="What email domains are allowed to have access to this branch" panRole [roles]="['admin']">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'enforce_domain_security')"
                [ngModel]="branchData.keystore?.enforce_domain_security ?? false"
              />

              Domain Security

              <app-pan-button size="sm" icon="newspaper" color="info" (click)="branchWhitelistModal.show()"></app-pan-button>
            </label>

            <label class="w-[30%]" matTooltip="Hide this branch from the Incident Submission tab">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'disable_incident_submission')"
                [ngModel]="branchData.keystore?.disable_incident_submission ?? false"
              />
              Hide in Incident Submission
            </label>
          </div>
        </fieldset>

        <fieldset class="border border-solid border-gray-300 p-3">
          <legend class="font-bold">Branch Opt Outs</legend>

          <div class="flex flex-wrap gap-2">
            <label class="w-[30%]" matTooltip="Opt out of Rhombus passwords">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'rhombus_password_opt_out')"
                [ngModel]="branchData.keystore?.rhombus_password_opt_out ?? false"
              />
              Rhombus Opt Out
            </label>

            <label class="w-[30%]" matTooltip="Opt out of theft report emails">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="updateEmailOptOut($event)"
                [ngModel]="branchData.keystore?.theft_report_options?.skip_email ?? false"
              />
              Email Opt Out
            </label>

            <label class="w-[30%]" matTooltip="Opt out of sending access granted emails">
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'access_granted_email_opt_out')"
                [ngModel]="branchData.keystore?.access_granted_email_opt_out ?? false"
              />
              Access Granted Email Opt Out
            </label>

            <label
              class="w-[30%]"
              matTooltip="Opt out of Guest OTP for shared Suspicious Incident Reports. Guests with a valid shared link open the report directly without OTP authentication."
            >
              <input
                type="checkbox"
                panCheckbox
                (ngModelChange)="setBranchSettingEnabled($event, 'guest_otp_opt_out')"
                [ngModel]="branchData.keystore?.guest_otp_opt_out ?? false"
              />
              Guest OTP Opt Out
            </label>
          </div>
        </fieldset>
      </div>
    </div>
  </div>
}

<app-pan-modal
  #branchWhitelistModal
  [topLevel]="false"
  [shouldBodyScroll]="false"
  size="sm"
  (modalOpen)="onBranchWhitelistModalOpen()"
  (modalClose)="onBranchWhitelistModalClose()"
>
  <div header>Email Domain Whitelist</div>
  <div body>
    <ng-select
      class="w-full mb-2"
      placeholder="Enter domains..."
      [multiple]="true"
      [addTag]="addTagFn"
      [virtualScroll]="true"
      [(ngModel)]="whitelistDomains"
    >
    </ng-select>
  </div>
  <div footer>
    <app-pan-button color="primary" (click)="confirmBranchWhitelistDetails()"> Confirm Domains</app-pan-button>
  </div>
</app-pan-modal>

<swal
  #facialRecognition
  title="Confirm Facial Recognition Status Change"
  text="In case of enabling Facial Recognition:
        Make sure that the operator has agreed to have facial recognition services provided to the branch.
        (This feature is illegal to use in certain situations.)
        In case of disabling Facial Recognition:
        Facial Recognition Services will not be provided for the branch."
  icon="warning"
  [showCancelButton]="true"
  [focusCancel]="true"
  (confirm)="updateFacialRecognition()"
  confirmButtonText="Yes, confirm change"
>
</swal>

<swal
  #suspendAccess
  title="Confirm Branch Access Suspension"
  text="Suspending access blocks operational branch and location data and pauses all active plans. Restoring access resumes the plans without changing user roles."
  icon="warning"
  [showCancelButton]="true"
  [focusCancel]="true"
  (confirm)="setBranchSettingEnabled(!(branchData.keystore?.suspend_access ?? false), 'suspend_access')"
  confirmButtonText="Yes, confirm change"
>
</swal>

<swal
  #dollarsHide
  title="Confirm $ Hidden Change"
  text="This hides or unhides the currency value in Theft Reports."
  icon="warning"
  [showCancelButton]="true"
  [focusCancel]="true"
  (confirm)="setBranchSettingEnabled(!(branchData.keystore?.hide_currency ?? false), 'hide_currency')"
  confirmButtonText="Yes, confirm change"
>
</swal>

<swal
  #disableNoneTemplate
  title="Confirm Disable None Template Change"
  text="This hides or unhides the None template in Incident Submission tab."
  icon="warning"
  [showCancelButton]="true"
  [focusCancel]="true"
  (confirm)="setBranchSettingEnabled(!(branchData.keystore?.disable_none_template ?? false), 'disable_none_template')"
>
  confirmButtonText="Yes, confirm change"
</swal>

<swal
  #requiresQa
  title="Confirm Require QA"
  text="This feature requires incident submissions to undergo Panoptyc QA."
  icon="warning"
  [showCancelButton]="true"
  [focusCancel]="true"
  (confirm)="setBranchSettingEnabled(!(branchData.keystore?.incident_submission_requires_qa ?? false), 'incident_submission_requires_qa')"
  confirmButtonText="Yes, confirm change"
>
</swal>

<swal
  #removeCustomerSwal
  title="Remove Customer Tag"
  [text]="getRemoveCustomerTagText(branchCustomer())"
  icon="warning"
  [showCancelButton]="true"
  [focusCancel]="true"
  (confirm)="removeCustomerById(branchCustomer()?.id)"
  confirmButtonText="Yes, Delete"
>
</swal>
